/**
 * §9.1 Hero — "Let's Build Your AI Trading Business Together".
 * Left: title, subtitle, CTAs.
 * Right: animated communication hub — Kalki Ai Cloud at the centre with
 * five spokes pulsing outward to Sales / Success / Support /
 * Partnerships / Consulting nodes on a circular perimeter.
 */

import React from 'react'
import {
  Calendar, Handshake, Sparkles, LifeBuoy, Puzzle, Compass, Building2, ArrowRight,
} from 'lucide-react'
import {
  Container, Eyebrow, GradientButton, GhostButton,
} from '@/components/landing/design-system'

const NODES = [
  { id: 'sales',   label: 'Sales',           iconKey: 'Handshake', tone: '#FFC107' },
  { id: 'success', label: 'Success',         iconKey: 'Sparkles',   tone: '#22c55e' },
  { id: 'support', label: 'Support',         iconKey: 'LifeBuoy',  tone: '#3b82f6' },
  { id: 'partner', label: 'Partnerships',    iconKey: 'Puzzle',    tone: '#a855f7' },
  { id: 'consult', label: 'Consulting',      iconKey: 'Compass',   tone: '#22d3ee' },
]
const NODE_ICONS = { Handshake, Sparkles, LifeBuoy, Puzzle, Compass }

export default function Hero() {
  const R = 155
  const cx = 280, cy = 200

  return (
    <section className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.55]"
        style={{ background: 'radial-gradient(60% 45% at 50% 20%, rgba(255,193,7,0.14), transparent 70%)' }} />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_0%,rgba(0,0,0,0.4)_70%,rgba(0,0,0,1)_100%)]" />

      <Container size="wide">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* LEFT — copy */}
          <div className="lg:col-span-6 flex flex-col gap-6">
            <Eyebrow>Contact &amp; Sales</Eyebrow>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.02]">
              Let&apos;s build your AI trading business{' '}
              <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">together.</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
              Whether you&apos;re evaluating Kalki Ai Cloud for the first time, planning an enterprise
              deployment, or looking for technical assistance, our team is here to help. Choose
              the path that matches where you are.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-3 pt-2">
              <GradientButton href="#demo" size="lg">
                <Calendar className="h-4 w-4" /> Schedule a live demo
              </GradientButton>
              <GhostButton href="#sales" size="lg" trailingIcon>
                <Handshake className="h-4 w-4" /> Contact sales
              </GhostButton>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {[
                { l: 'Response within 1 business day' },
                { l: 'Global coverage · 24×7 Enterprise' },
                { l: 'Dedicated enterprise desk' },
              ].map((t) => (
                <span key={t.l} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11.5px] font-semibold text-zinc-300">
                  <Sparkles className="h-3 w-3 text-brand-gold" /> {t.l}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT — communication hub */}
          <div className="lg:col-span-6">
            <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-4 md:p-5 backdrop-blur-xl overflow-hidden">
              <div className="pointer-events-none absolute inset-0 opacity-70"
                style={{ background: 'radial-gradient(60% 40% at 50% 50%, rgba(255,193,7,0.10), transparent 70%)' }} />

              <svg viewBox="0 0 560 400" className="relative w-full h-auto">
                <defs>
                  {NODES.map((n) => (
                    <linearGradient key={n.id} id={`ch-${n.id}`} x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%"   stopColor={n.tone} stopOpacity="0.7" />
                      <stop offset="100%" stopColor={n.tone} stopOpacity="0.1" />
                    </linearGradient>
                  ))}
                  <radialGradient id="ch-center" cx="50%" cy="50%" r="50%">
                    <stop offset="0%"   stopColor="#FFC107" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#FFC107" stopOpacity="0" />
                  </radialGradient>
                </defs>

                {/* orbits (decorative) */}
                <circle cx={cx} cy={cy} r={R + 20} fill="none" stroke="rgba(255,255,255,0.05)" strokeDasharray="3 6" />
                <circle cx={cx} cy={cy} r={R - 30} fill="none" stroke="rgba(255,255,255,0.04)" />

                {/* spokes + moving packets */}
                {NODES.map((n, i) => {
                  const angle = (i * 2 * Math.PI) / NODES.length - Math.PI / 2
                  const x = cx + Math.cos(angle) * R
                  const y = cy + Math.sin(angle) * R
                  const pathId = `ch-path-${n.id}`
                  const Icon = NODE_ICONS[n.iconKey] || Sparkles
                  return (
                    <g key={n.id}>
                      <path id={pathId} d={`M ${cx} ${cy} L ${x} ${y}`} stroke={`url(#ch-${n.id})`} strokeWidth="1.5" strokeDasharray="3 6" fill="none" className="animate-brand-dash" />
                      <circle r="3" fill={n.tone} opacity="0.9">
                        <animateMotion dur={`${3 + i * 0.4}s`} repeatCount="indefinite"><mpath href={`#${pathId}`} /></animateMotion>
                      </circle>

                      {/* node */}
                      <g transform={`translate(${x} ${y})`}>
                        <circle r="28" fill="rgba(10,10,12,0.9)" stroke={n.tone} strokeOpacity="0.55" strokeWidth="1.4" />
                        <circle r="25" fill={`${n.tone}12`} />
                        <foreignObject x="-13" y="-13" width="26" height="26">
                          <div className="flex items-center justify-center w-full h-full" style={{ color: n.tone }}>
                            <Icon strokeWidth={1.8} style={{ width: 13, height: 13 }} />
                          </div>
                        </foreignObject>
                        <text y="48" textAnchor="middle" fontSize="11" fontWeight="600" fill="rgba(255,255,255,0.9)">{n.label}</text>
                      </g>
                    </g>
                  )
                })}

                {/* centre — Kalki Ai Cloud */}
                <g transform={`translate(${cx} ${cy})`}>
                  <circle r="85" fill="url(#ch-center)" />
                  <circle r="50" fill="rgba(10,10,12,0.9)" stroke="rgba(255,193,7,0.4)" strokeWidth="1.5" />
                  <circle r="46" fill="rgba(255,193,7,0.08)" />
                  <foreignObject x="-22" y="-22" width="44" height="44">
                    <div className="flex items-center justify-center w-full h-full text-brand-gold">
                      <Building2 strokeWidth={1.8} style={{ width: 22, height: 22 }} />
                    </div>
                  </foreignObject>
                  <text y="72" textAnchor="middle" fontSize="12" fontWeight="700" fill="rgba(255,255,255,0.95)">Kalki Ai Cloud</text>
                </g>
              </svg>

              <div className="relative mt-2 flex items-center justify-between rounded-md border border-emerald-400/20 bg-emerald-400/[0.05] px-3 py-2">
                <div className="flex items-center gap-2 text-[10.5px] text-emerald-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span><span className="text-white font-semibold">Live</span> · team routes each enquiry to the right desk</span>
                </div>
                <div className="flex items-center gap-1 text-[10.5px] text-emerald-400 font-mono">
                  1 business day <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
